import React, { useState, useRef, useEffect, createRef } from 'react';
import PropTypes from 'prop-types';
import style from './ContentMenu.module.less';
import clsx from 'clsx';
import AppLink from '../../links/AppLink';
import { SvgIcon } from 'edcom-ui';
import {
  MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID,
  tabInfoPropType,
} from '../util';

export default function ContentMenu({ tabInfo, backToMenu }) {
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState(null);
  const sectionsWithGradeAndRefs = useRef({});

  // Trigger the initial animation (sliding down the first section) when the component loads
  useEffect(() => setActiveSubmenuIndex(0), []);

  // Trigger a rerender if the height of the current active Submenu has changed, so we update its height
  useEffect(() => {
    const resizeSubmenu = () => {
      if (activeSubmenuIndex !== null) {
        const sectionRef =
          sectionsWithGradeAndRefs.current[activeSubmenuIndex].ref.current;

        if (
          sectionRef.prevHeight &&
          sectionRef.prevHeight !== sectionRef.offsetHeight
        ) {
          // Force a re-render (ugly but works)
          setActiveSubmenuIndex(null);
          setActiveSubmenuIndex(activeSubmenuIndex);
        }

        // Whenever we resize the window, save the current height of the ref as prevHeight so that, if it changes, we
        // can trigger the re-render above
        sectionRef.prevHeight = sectionRef.offsetHeight;
      }
    };

    // Since the sections can change height because we update the font-size for mobile widths, we need to trigger a
    // rerender if the height of the submenu changed. To do this, add an event listener on resize and an ugly function
    window.addEventListener('resize', resizeSubmenu);
    return () => window.removeEventListener('resize', resizeSubmenu);
  }, [activeSubmenuIndex]);

  const { name, gradesForContentType, sections, seeAll } = tabInfo;

  const hasSeeAll = seeAll && seeAll.url;

  /**
   * Builds an array for the different grades of this content in the shape of the sections elements. This way, we can
   * build them together. Also, add a "ref" property to each section, so we can... well, reference it, so we can keep
   * track of the sections heights (see comments in the useEffect above)
   * @returns [{items, title, ref}]
   */
  const getSectionsWithGradeAndRefs = () => {
    let allSections = sections;

    // If there are grades for this content, add them as if they were any other section since the styling is the same
    if (gradesForContentType) {
      const gradesSectionItems = gradesForContentType.map(
        ({ longDisplay, urlName }) => ({
          title: longDisplay,
          url: `/${tabInfo.urlName}/${urlName}/`,
          column: 1,
          type: 'Main Item',
          icon: '',
        })
      );

      allSections = [
        { title: 'By Grade', items: gradesSectionItems },
        ...sections,
      ];
    }

    return allSections.map(section => ({ ...section, ref: createRef() }));
  };

  // Initialize the sectionsWithGradeAndRefs ref (this will happen only once)
  if (!sectionsWithGradeAndRefs.current.length) {
    sectionsWithGradeAndRefs.current = getSectionsWithGradeAndRefs();
  }

  // Define it outside since we show this link in two places duplicated
  const seeAllLink = hasSeeAll && (
    <AppLink href={seeAll.url} className={style.browseAll}>
      <span>{seeAll.title}</span> <SvgIcon name="arrowRight" aria-hidden />
    </AppLink>
  );

  return (
    <div className={style.contentMenu}>
      <div className={style.displayHeader}>
        <div className={clsx(style.header, !hasSeeAll && style.noSeeAllLink)}>
          <button
            id={MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID}
            className={clsx(style.backIconContainer, style.emptyButton)}
            type="button"
            aria-label="Back"
            onClick={backToMenu}
          >
            <SvgIcon name="angleLeft" aria-hidden />
          </button>
          <h3 className={style.title}>{name}</h3>
        </div>
        {hasSeeAll && <div className={style.browseAllTop}>{seeAllLink}</div>}
      </div>

      <div className={style.sectionsContainer}>
        {sectionsWithGradeAndRefs.current.map((section, sectionIndex) => {
          const isOpen = activeSubmenuIndex === sectionIndex;
          const { title } = section;

          return (
            <div
              key={title}
              className={clsx(style.section, isOpen && style.open)}
            >
              <button
                className={clsx(style.sectionHeader, style.emptyButton)}
                aria-label={`${isOpen ? 'Close' : 'Expand'} ${title}`}
                aria-expanded={isOpen}
                type="button"
                onClick={() =>
                  setActiveSubmenuIndex(isOpen ? null : sectionIndex)
                }
              >
                <h4 className={style.sectionTitle}>{title}</h4>
                <SvgIcon name={isOpen ? 'minus' : 'plus'} aria-hidden />
              </button>

              <div
                className={style.sectionItemsContainer}
                aria-hidden={!isOpen}
                style={
                  isOpen ? { height: section.ref.current.offsetHeight } : null
                }
              >
                <ul className={style.sectionItems} ref={section.ref}>
                  {section.items.map(item => (
                    <li
                      key={item.url}
                      className={clsx(
                        item.type === 'Main Item' && style.mainItem
                      )}
                    >
                      <AppLink href={item.url}>{item.title}</AppLink>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}

        <div className={hasSeeAll && style.browseAllBottom}>{seeAllLink}</div>
      </div>
    </div>
  );
}

ContentMenu.propTypes = {
  tabInfo: tabInfoPropType.isRequired,
  backToMenu: PropTypes.func.isRequired,
};
